import styles from "./multi-calendar-button.module.css";

import Button from "@mui/material/Button";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { UseDateFieldProps } from "@mui/x-date-pickers/DateField";
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
} from "@mui/x-date-pickers/models";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useEffect, useState } from "react";
import { Moment } from "moment";
import { Calendar } from "../../../../../components/svg";
import { PickersDay } from "@mui/x-date-pickers";
import moment from "moment";

interface ButtonFieldProps
  extends UseDateFieldProps<Moment, false>,
    BaseSingleInputFieldProps<
      Moment | null,
      Moment,
      FieldSection,
      false,
      DateValidationError
    > {}

export interface MultiCalendarButtonProps {
  value: (Moment | null)[];
  label: string;
  dayLimit: number;
  forcedRange?: number;
  onChange(timestamp: (Moment | null)[]): void;
  shouldDisableDate(date: Moment): boolean;
}

export default function MultiCalendarButton(props: MultiCalendarButtonProps) {
  const { onChange, value, shouldDisableDate, label, dayLimit, forcedRange } =
    props;

  const [firstDay, setFirstDay] = useState<Moment | null>(null);
  const [secondDay, setSecondDay] = useState<Moment | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setFirstDay(value[0]);
    setSecondDay(value[1]);
  }, []);

  const DayField = (props: any) => {
    let selected = props.selected;
    if (props.day.isSame(firstDay, "day")) selected = true;
    if (props.day.isSame(secondDay, "day")) selected = true;
    if (
      firstDay &&
      secondDay &&
      props.day.isBetween(firstDay, secondDay, "days")
    )
      selected = true;

    return (
      <PickersDay
        {...props}
        selected={selected}
        onMouseOver={() => {}}
        onDaySelect={(value) => {
          if (forcedRange) {
            if (
              moment(value)
                .add(forcedRange - 1, "days")
                .isAfter(moment(), "day")
            ) {
              setFirstDay(moment().subtract(forcedRange - 1, "days"));
              setSecondDay(moment());
              setOpen(false);
              onChange([moment().subtract(forcedRange - 1, "days"), moment()]);
              return;
            }

            setFirstDay(value);
            setSecondDay(moment(value).add(forcedRange - 1, "days"));
            onChange([value, moment(value).add(forcedRange - 1, "days")]);
            setOpen(false);
            return;
          }
          if (firstDay === null) {
            setFirstDay(value);
            return;
          }
          if (secondDay === null) {
            setSecondDay(value);
            setOpen(false);
            onChange([firstDay, value]);
            return;
          }
          setFirstDay(null);
          setSecondDay(null);
          setFirstDay(value);
        }}
      />
    );
  };

  const ButtonField = (props: ButtonFieldProps) => {
    const {
      label,
      disabled,
      InputProps: { ref } = {},
      inputProps: { "aria-label": ariaLabel } = {},
    } = props;

    return (
      <Button
        variant="outlined"
        disabled={disabled}
        ref={ref}
        aria-label={ariaLabel}
        onClick={() => {
          setOpen?.((prev) => !prev);
        }}
        startIcon={<Calendar className={styles["calendar-icon"]} />}
        className={styles["calendar-button"]}
      >
        <div className={styles["label"]}>{label ? `${label}` : null}</div>
      </Button>
    );
  };

  const ButtonDatePicker = (
    props: Omit<DatePickerProps<Moment>, "open" | "onOpen" | "onClose">
  ) => {
    return (
      <DatePicker
        closeOnSelect={false}
        slots={{ day: DayField, field: ButtonField, ...props.slots }}
        {...props}
        open={open}
        className={styles["calendar-picker"]}
        onClose={() => {
          setOpen(false);
        }}
        onOpen={() => setOpen(true)}
        reduceAnimations={true}
      />
    );
  };

  const dayDisabled = (date: Moment) => {
    if (shouldDisableDate(date)) {
      return true;
    }
    if (firstDay && !secondDay) {
      if (
        date.isBefore(firstDay, "day") ||
        date.isAfter(moment(firstDay).add(dayLimit - 1, "day"))
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale={navigator.language.substring(0, 2)}
    >
      <div className={styles["calendar-button-container"]}>
        <ButtonDatePicker
          label={label}
          value={firstDay}
          shouldDisableDate={dayDisabled}
        />
      </div>
    </LocalizationProvider>
  );
}
